import { gql, useQuery } from '@apollo/client';

import type {
  GetDealWireDetails,
  GetDealWireDetailsVariables,
} from './__generated__/getDealWireDetails_gql';
/**
 * Fetches deal cards to read wire_details and can_edit_wire_details from the WireConfirmationCard.
 * Used by the remittance cashflow sidecar to prefill and optionally save wire details.
 */
export const GET_DEAL_WIRE_DETAILS = gql`
  query GetDealWireDetails($dealId: ID!) {
    deal(id: $dealId) {
      id
      role
      listing {
        name
      }
      cards {
        __typename
        ... on WireConfirmationCard {
          wire_details {
            account_name
            account_number
            bank_name
            routing_number
          }
        }
      }
      bids {
        price {
          transfer_amount
        }
        stipulations {
          transfer_amount
        }
      }
    }
  }
`;

export type WireDetailsShape = {
  account_name: string;
  account_number: string;
  bank_name: string;
  routing_number: string;
};

export type DealRole = 'BUYER' | 'SELLER';

export type DealWireDetailsResult = {
  wireDetails: WireDetailsShape | null;
  canEditWireDetails: boolean;
  shouldShowWireDetails: boolean;
  dealRole: DealRole | undefined;
  /** Deal bid transfer amount (0–100). Investor share; servicer gets 100 - this. Default 100. */
  transferPercent: number;
};

type WireConfirmationCardFields = {
  __typename: 'WireConfirmationCard';
  wire_details?: WireDetailsShape | null;
  can_edit_wire_details?: boolean;
  should_show_wire_details?: boolean;
};

type DealWithBids = GetDealWireDetails['deal'] & {
  bids?: Array<{
    price?: { transfer_amount?: number | null } | null;
    stipulations?: { transfer_amount?: number | null } | null;
  }> | null;
};

function getTransferPercentFromDeal(deal: DealWithBids | null | undefined): number {
  const bids = deal?.bids;
  const first = Array.isArray(bids) && bids.length > 0 ? bids[0] : undefined;
  const fromPrice = first?.price?.transfer_amount;
  const fromStip = first?.stipulations?.transfer_amount;
  const raw = fromPrice ?? fromStip ?? 100;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : 100;
}

function extractWireDetailsFromDeal(
  deal: GetDealWireDetails['deal'] | null | undefined,
): DealWireDetailsResult {
  const dealRole = deal?.role != null ? (deal.role as DealRole) : undefined;
  const cards = (deal?.cards ?? []) as unknown[];
  const card = cards.find(
    (c) => (c as { __typename?: string })?.__typename === 'WireConfirmationCard',
  ) as WireConfirmationCardFields | undefined;
  const transferPercent = getTransferPercentFromDeal(deal as DealWithBids);
  if (!card) {
    return {
      wireDetails: null,
      canEditWireDetails: false,
      shouldShowWireDetails: false,
      dealRole,
      transferPercent,
    };
  }
  return {
    wireDetails: card.wire_details ?? null,
    canEditWireDetails: card.can_edit_wire_details ?? false,
    shouldShowWireDetails: card.should_show_wire_details ?? false,
    dealRole,
    transferPercent,
  };
}

export function useDealWireDetails(dealId: string | undefined) {
  const skip = !dealId?.trim();
  const { data, loading, error, refetch } = useQuery<
    GetDealWireDetails,
    GetDealWireDetailsVariables
  >(GET_DEAL_WIRE_DETAILS, {
    variables: { dealId: dealId ?? '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const extracted = extractWireDetailsFromDeal(data?.deal ?? null);
  const dealWithListing = data?.deal as { listing?: { name?: string | null } | null } | null;
  const listingName = dealWithListing?.listing?.name ?? null;

  return {
    ...extracted,
    listingName: listingName ?? undefined,
    loading,
    error,
    refetch,
  };
}
